import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  DEFAULT_LOCALE,
  LS_KEY,
  addLocalePrefix,
  detectPreferredLocale,
  localeFromPath,
} from "@/lib/locale";

/**
 * On the FIRST page load only, redirect to /zh/... if:
 *   - user has no localStorage override AND
 *   - navigator.language starts with 'zh'.
 *
 * Fires exactly once per session-mount. Does NOT trigger the loading screen.
 * Uses <Navigate replace/> so no history entry is added.
 */
export function FirstVisitRedirect({ children }) {
  const { pathname, search, hash } = useLocation();
  const currentLocale = localeFromPath(pathname);

  // Only consider redirecting on the very first render. We tag sessionStorage
  // so refresh / SPA-navigation don't re-run detection.
  const alreadyChecked = React.useMemo(() => {
    try {
      return window.sessionStorage.getItem("frac.locale.checked") === "1";
    } catch (_) {
      return true;
    }
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem("frac.locale.checked", "1");
    } catch (_) {
      // ignore.
    }
  }, []);

  if (alreadyChecked) return children;

  const preferred = detectPreferredLocale();
  if (preferred === currentLocale || preferred === DEFAULT_LOCALE) return children;

  // preferred === 'zh' and we're on '/'-style — redirect to the /zh mirror.
  const canonical = pathname; // Already a canonical (en) path since currentLocale === 'en'.
  const target = addLocalePrefix(canonical, preferred);
  return <Navigate to={`${target}${search || ""}${hash || ""}`} replace />;
}

// Convenience export so tests can seed / clear the localStorage key.
export { LS_KEY };

export default FirstVisitRedirect;
